import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Landmark, Scale } from 'lucide-react';
import { ajoApi, AjoLedgerLine, AjoWalletGroup, AjoWalletSummary } from '../../api/ajo';
import { isApprovalRequired } from '../../api/approvals';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import { useToast } from '../../hooks/useToast';

const naira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-NG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : 'Never';

const KIND_LABEL: Record<string, string> = {
  contribution: 'Contribution',
  guest_sweep: 'Guest contribution',
  payout: 'Payout',
  cover: 'Organiser cover',
  repay: 'Cover repaid',
  arrears: 'Late share',
  refund: 'Refund',
  late_fee: 'Late fee',
  exit_share: 'Exit share',
  deposit: 'Deposit',
  deposit_return: 'Deposit returned',
  cover_levy: 'Cover pool levy',
  opening: 'Opening balance',
  pool_move: 'Moved into the wallet',
};

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, verticalAlign: 'top', whiteSpace: 'nowrap' };

/**
 * The PayIQ Ajo wallet: one account holding every group's money.
 *
 * What the bank says the wallet holds, beside what its groups hold between
 * them by their ledgers. The two should be equal; the difference is shown
 * so a gap is seen the day it appears. Under that, every group's money:
 * what came in, what went out, what is on its way, and its movements one
 * by one. Groups still on an account of their own can be moved in.
 */
export const AjoWalletTab: React.FC<{
  canOperate: boolean;
  /** Setting or opening the wallet account: a super admin, straight through. */
  canSetAccount: boolean;
}> = ({ canOperate, canSetAccount }) => {
  const toast = useToast();
  const [summary, setSummary] = useState<AjoWalletSummary | null>(null);
  const [open, setOpen] = useState<AjoWalletGroup | null>(null);
  const [moving, setMoving] = useState<AjoWalletGroup | null>(null);
  const [busy, setBusy] = useState(false);
  const [settingAccount, setSettingAccount] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountId, setAccountId] = useState('');
  const [opening, setOpening] = useState(false);

  async function saveAccount() {
    setBusy(true);
    try {
      const result = await ajoApi.setWalletAccount(accountNumber.trim(), accountId.trim() || undefined);
      toast.success(
        `The wallet now uses ${result.accountNumber}${result.accountName ? ` (${result.accountName})` : ''}.`,
      );
      setSettingAccount(false);
      setAccountNumber('');
      setAccountId('');
      await load();
    } catch (err) {
      toast.error(err, 'Could not set that account.');
    } finally {
      setBusy(false);
    }
  }

  async function openAccount() {
    setBusy(true);
    try {
      const result = await ajoApi.openWalletAccount();
      toast.success(`Opened ${result.accountNumber} for the wallet.`);
      setOpening(false);
      await load();
    } catch (err) {
      toast.error(err, 'Could not open an account.');
    } finally {
      setBusy(false);
    }
  }

  const load = useCallback(async () => {
    try {
      setSummary(await ajoApi.getWallet());
    } catch (err) {
      toast.error(err, 'Could not load the Ajo wallet.');
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function move() {
    if (!moving) return;
    setBusy(true);
    try {
      const result = await ajoApi.moveToWallet(moving.id);
      if (isApprovalRequired(result)) {
        toast.success('Sent for approval. It moves once another admin approves it.');
      } else if (result.status === 'pending') {
        toast.success('The bank has not finished the transfer. Try again in a few minutes.');
      } else {
        toast.success(
          `${moving.name} is in the wallet: ${naira(result.moved)} moved` +
            (result.leftBehind > 0 ? `, ${naira(result.leftBehind)} left in its old account.` : '.'),
        );
      }
      setMoving(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not move that group.');
    } finally {
      setBusy(false);
    }
  }

  if (!summary) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  const w = summary.wallet;
  const pooled = summary.groups.filter((g) => g.pooled);
  const own = summary.groups.filter((g) => !g.pooled);
  const matches = w?.difference !== null && w?.difference !== undefined && Math.abs(w.difference) < 0.01;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--neutral-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              PayIQ Ajo wallet
            </div>
            {w ? (
              <>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
                  {w.bankBalance === null ? 'Balance not read' : naira(w.bankBalance)}
                </div>
                <div style={{ fontSize: 13, color: 'var(--neutral-600)', marginTop: 4 }}>
                  {w.accountName ? `${w.accountName} · ` : ''}
                  {w.accountNumber} · Safe Haven MFB ·{' '}
                  {w.source === 'env' ? 'set in the environment' : 'set in admin'}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, color: 'var(--neutral-700)', marginTop: 6, maxWidth: 560 }}>
                No account yet. Every group's money will be held in one account once you set it. Until then, new
                groups keep an account of their own.
              </div>
            )}
          </div>
          {canSetAccount && w?.source !== 'env' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={() => setSettingAccount(true)}>
                {w ? 'Change account' : 'Set account'}
              </Button>
              {!w ? <Button onClick={() => setOpening(true)}>Open a new account</Button> : null}
            </div>
          ) : null}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <StatCard
          label="Wallet at the bank"
          value={w ? (w.bankBalance === null ? 'Not read' : naira(w.bankBalance)) : 'Not opened yet'}
          icon={Landmark}
          accent="blue"
        />
        <StatCard label="Held for groups" value={w ? naira(w.owedToGroups) : naira(0)} icon={Scale} accent="purple" />
        <StatCard
          label="Difference"
          value={w?.difference === null || !w ? 'Not known' : naira(w.difference)}
          icon={matches ? ArrowDownLeft : ArrowUpRight}
          accent={matches ? 'green' : 'neutral'}
        />
        <StatCard label="Groups in the wallet" value={`${pooled.length} of ${summary.groups.length}`} accent="neutral" />
      </div>
      {w ? (
        <p style={{ margin: 0, fontSize: 13, color: matches ? 'var(--green-900)' : 'var(--red-700, #b42318)' }}>
          {w.difference === null
            ? `Wallet ${w.accountNumber}. The bank could not be asked for its balance just now.`
            : matches
              ? `Wallet ${w.accountNumber} holds exactly what its groups are owed.`
              : `Wallet ${w.accountNumber} holds ${naira(Math.abs(w.difference))} ${w.difference > 0 ? 'more' : 'less'} than its groups are owed. ${
                  w.difference > 0
                    ? 'Usually money on its way out that the bank has already taken, or a fee top-up.'
                    : 'Look at the groups with money in flight, and the bank statement.'
                }`}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>
          The wallet opens the first time a new group needs it.
        </p>
      )}

      <GroupTable
        title="In the PayIQ Ajo wallet"
        groups={pooled}
        empty="No group uses the wallet yet. New groups start in it."
        onOpen={setOpen}
      />
      <GroupTable
        title="Still on an account of their own"
        groups={own}
        empty="Every group is in the wallet."
        onOpen={setOpen}
        onMove={canOperate ? setMoving : undefined}
      />

      <LedgerDrawer group={open} onClose={() => setOpen(null)} />

      <Modal
        isOpen={settingAccount}
        onClose={() => setSettingAccount(false)}
        title={w ? 'Change the wallet account' : 'Set the wallet account'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>
            A Safe Haven account PayIQ holds. Every group in the wallet is paid into and out of it. It is checked with
            the bank before it is used, and it cannot be changed while groups in the wallet hold money.
          </p>
          <Input
            label="Account number"
            value={accountNumber}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
          />
          <Input
            label="Safe Haven account ID (optional)"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setSettingAccount(false)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={busy || accountNumber.length !== 10} onClick={() => void saveAccount()}>
              Use this account
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={opening}
        title="Open a new account for the wallet?"
        message="PayIQ opens a new account at Safe Haven, under its own profile, and every new group's money is held in it."
        confirmLabel="Open account"
        loading={busy}
        onConfirm={() => void openAccount()}
        onCancel={() => setOpening(false)}
      />

      <ConfirmDialog
        isOpen={!!moving}
        title={`Move ${moving?.name ?? 'this group'} into the wallet?`}
        message={`What its members are owed (${moving ? naira(moving.balance) : '₦0'}) moves from its own account into the PayIQ Ajo wallet, and every payment after that goes through the wallet. If its account holds less than that, nothing moves.`}
        confirmLabel="Move into the wallet"
        loading={busy}
        onConfirm={() => void move()}
        onCancel={() => setMoving(null)}
      />
    </div>
  );
};

function GroupTable({
  title,
  groups,
  empty,
  onOpen,
  onMove,
}: {
  title: string;
  groups: AjoWalletGroup[];
  empty: string;
  onOpen: (g: AjoWalletGroup) => void;
  onMove?: (g: AjoWalletGroup) => void;
}) {
  return (
    <Card padding={0}>
      <div style={{ padding: '14px 16px' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>
      </div>
      {groups.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--neutral-200)' }}>
                {['Group', 'Holds', 'Can spend', 'Money in', 'Money out', 'In flight', 'Last movement', ''].map((h) => (
                  <th key={h} style={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{g.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                      {g.status} · {g.movements} {g.movements === 1 ? 'movement' : 'movements'}
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{naira(g.balance)}</td>
                  <td style={td}>{naira(g.available)}</td>
                  <td style={{ ...td, color: 'var(--green-900)' }}>{naira(g.moneyIn)}</td>
                  <td style={td}>{naira(g.moneyOut)}</td>
                  <td style={td}>
                    {g.inFlight > 0 ? <Badge variant="warning">{naira(g.inFlight)}</Badge> : 'None'}
                  </td>
                  <td style={td}>{when(g.lastMovementAt)}</td>
                  <td style={{ ...td, display: 'flex', gap: 6 }}>
                    <Button size="sm" variant="ghost" onClick={() => onOpen(g)}>
                      Movements
                    </Button>
                    {onMove ? (
                      <Button size="sm" variant="secondary" onClick={() => onMove(g)}>
                        Move into wallet
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function LedgerDrawer({ group, onClose }: { group: AjoWalletGroup | null; onClose: () => void }) {
  const toast = useToast();
  const [lines, setLines] = useState<AjoLedgerLine[] | null>(null);

  useEffect(() => {
    if (!group) return;
    let live = true;
    setLines(null);
    ajoApi
      .getGroupLedger(group.id)
      .then((r) => live && setLines(r.items))
      .catch((err) => {
        if (live) {
          toast.error(err, 'Could not load the movements.');
          setLines([]);
        }
      });
    return () => {
      live = false;
    };
  }, [group, toast]);

  return (
    <Drawer
      isOpen={!!group}
      onClose={onClose}
      title={group?.name ?? ''}
      subtitle={group ? `Holds ${naira(group.balance)} · can spend ${naira(group.available)}` : undefined}
      width={640}
    >
      {lines === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spinner />
        </div>
      ) : lines.length === 0 ? (
        <EmptyState title="No movements yet." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {lines.map((l) => (
            <div
              key={l.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 0',
                borderBottom: '1px solid var(--neutral-200)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{KIND_LABEL[l.kind] ?? l.kind}</div>
                <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  {when(l.createdAt)} · {l.reference}
                </div>
                {l.error ? <div style={{ fontSize: 12, color: 'var(--red-700, #b42318)' }}>{l.error}</div> : null}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: l.groupDelta > 0 ? 'var(--green-900)' : 'var(--neutral-800)',
                  }}
                >
                  {l.groupDelta > 0 ? '+' : l.groupDelta < 0 ? '−' : ''}
                  {naira(Math.abs(l.groupDelta || l.amount))}
                </div>
                <Badge
                  variant={
                    l.status === 'posted'
                      ? 'success'
                      : l.status === 'failed'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  {l.status === 'posted'
                    ? 'Done'
                    : l.status === 'failed'
                      ? 'Refused'
                      : l.status === 'unknown'
                        ? 'No answer'
                        : 'Sending'}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}
